import React from 'react';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import { login, logout } from './utils/auth';

// Import route components with lazy loading is handled in routes/index.js
// We need to create wrapper components to maintain backward compatibility
// with existing component interfaces that use callback props

/**
 * LoginWrapper Component
 * Wraps the Login component to bridge React Router navigation
 * with the existing callback-based interface
 */
const LoginWrapper = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Import Login component dynamically to avoid circular dependency
  const Login = React.lazy(() => import('./components/Login').then(module => ({ default: module.default })));

  const handleLogin = () => {
    console.log('Login successful');
    // Simulate authentication - in production, this would call an API
    // Store a dummy token for demonstration
    login('dummy_token_' + Date.now(), { email: 'annette@gmail.com', name: 'Ellington Thom' });
    // Redirect to dashboard or the page the user was trying to access
    const from = location.state?.from?.pathname || '/dashboard';
    navigate(from, { replace: true });
  };

  const handleShowRegister = () => {
    navigate('/register');
  };

  return (
    <React.Suspense fallback={
      <div className="flex items-center justify-center min-h-screen bg-[#A5F3FC]">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    }>
      <Login
        onRegisterClick={handleShowRegister}
        onLoginClick={handleLogin}
      />
    </React.Suspense>
  );
};

/**
 * RegisterWrapper Component
 * Wraps the Register component to bridge React Router navigation
 * with the existing callback-based interface
 */
const RegisterWrapper = () => {
  const navigate = useNavigate();

  // Import Register component dynamically to avoid circular dependency
  const Register = React.lazy(() => import('./components/Register').then(module => ({ default: module.default })));

  const handleRegisterSubmit = () => {
    console.log('Registration submitted');
    // Simulate authentication - in production, this would call an API
    // Store a dummy token for demonstration
    login('dummy_token_' + Date.now(), { email: 'newuser@example.com', name: 'New User' });
    navigate('/dashboard', { replace: true });
  };

  const handleShowLogin = () => {
    navigate('/login');
  };

  return (
    <React.Suspense fallback={
      <div className="flex items-center justify-center min-h-screen bg-[#A5F3FC]">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    }>
      <Register
        onLoginClick={handleShowLogin}
        onRegisterSubmit={handleRegisterSubmit}
      />
    </React.Suspense>
  );
};

/**
 * DashboardWrapper Component
 * Wraps the Dashboard component to bridge React Router navigation
 * with the existing callback-based interface
 */
const DashboardWrapper = () => {
  const navigate = useNavigate();

  // Import Dashboard component dynamically to avoid circular dependency
  const Dashboard = React.lazy(() => import('./components/Dashboard').then(module => ({ default: module.default })));

  const handleLogout = () => {
    console.log('Logging out');
    // Clear authentication data
    logout();
    // Redirect to login page
    navigate('/login', { replace: true });
  };

  return (
    <React.Suspense fallback={
      <div className="flex items-center justify-center min-h-screen bg-[#A5F3FC]">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    }>
      <Dashboard handleLogout={handleLogout} />
    </React.Suspense>
  );
};

/**
 * Coming Soon Wrapper Component
 * Wraps the ComingSoon component for lazy loading
 */
const ComingSoonWrapper = () => {
  const ComingSoon = React.lazy(() => import('./components/ComingSoon').then(module => ({ default: module.default })));

  return (
    <React.Suspense fallback={
      <div className="flex items-center justify-center min-h-screen bg-[#A5F3FC]">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    }>
      <ComingSoon />
    </React.Suspense>
  );
};

/**
 * Main App Component
 * Sets up React Router with BrowserRouter and defines routes
 * Uses enterprise-standard practices:
 * - Lazy loading for route components
 * - Protected routes for authenticated pages
 * - Proper error handling
 * - Loading states
 * - Coming Soon flag check to redirect all routes to Coming Soon page
 */
function App() {
  // Check the Coming Soon flag from environment variable
  const comingSoonFlag = process.env.REACT_APP_COMING_SOON_FLAG;

  // If Coming Soon flag is set to "yes", show the Coming Soon page for all routes
  if (comingSoonFlag === 'yes') {
    return (
      <BrowserRouter>
        <div className="h-screen w-full bg-white">
          <ComingSoonWrapper />
        </div>
      </BrowserRouter>
    );
  }

  // Normal routing behavior when Coming Soon flag is not set or not "yes"
  return (
    <BrowserRouter>
      <div className="h-screen w-full bg-[#A5F3FC]">
        {/* Routes are defined inline here for simplicity, but could be moved to routes/index.js */}
        {/* Using React Router's Routes component for route configuration */}
        {React.createElement(
          require('react-router-dom').Routes,
          null,
          React.createElement(
            require('react-router-dom').Route,
            { path: '/', element: React.createElement(require('react-router-dom').Navigate, { to: '/dashboard', replace: true }) }
          ),
          React.createElement(
            require('react-router-dom').Route,
            { path: '/login', element: React.createElement(LoginWrapper) }
          ),
          React.createElement(
            require('react-router-dom').Route,
            { path: '/register', element: React.createElement(RegisterWrapper) }
          ),
          React.createElement(
            require('react-router-dom').Route,
            { 
              path: '/dashboard', 
              element: React.createElement(
                require('./components/ProtectedRoute').default,
                null,
                React.createElement(DashboardWrapper)
              )
            }
          ),
          React.createElement(
            require('react-router-dom').Route,
            { path: '*', element: React.createElement(require('react-router-dom').Navigate, { to: '/dashboard', replace: true }) }
          )
        )}
      </div>
    </BrowserRouter>
  );
}

export default App;
